// ids match the DeviceVerification final_status enum on the API
const STATUS_NAMES = {
    auto_check: 'Auto Check',
    broken_device: 'Broken device',
    consumption_too_low: 'Consumption too low',
    disconnected: 'Disconnected',
    info: 'Info',
    ok: 'OK',
    other: 'Other',
    replace: 'Replace',
    wise_metering: 'WiseMetering'
};

// auto_check is the server's own verdict — never offered as a choice, but it still needs a label.
const SELECTABLE_STATUSES = ['ok', 'wise_metering', 'consumption_too_low', 'disconnected', 'broken_device', 'other', 'info', 'replace'];

export const STATUS_OPTIONS = SELECTABLE_STATUSES.map(id => ({ id, name: STATUS_NAMES[id] }));

const OK_STATUSES = ['auto_check', 'info', 'ok'];

export const statusName = status => STATUS_NAMES[status] || '';

// The API validates state/final_status coherence: ok statuses require state ok, the rest not_ok.
export const stateForStatus = status => OK_STATUSES.includes(status) ? 'ok' : 'not_ok';

// A delta reading is measured against the one before it, so the first has nothing to compare with:
// a delta verification needs two checks before there is anything to report, an absolute one needs one.
export const checksNeeded = comparison => (comparison === 'delta' ? 2 : 1);

// One limit on both sides of the wire — the API's AUTO_CHECK_VARIATION_LIMIT.
export const VARIATION_LIMIT_PERCENT = 5;

export const isVariationWithinLimit = value => value !== null && value !== undefined && Math.abs(value) < VARIATION_LIMIT_PERCENT;

// A null is a declared absence — filled, in its own way.
export const isFilled = value => value !== undefined && value !== '';

// The API validates numbers by name — inputs hold strings, so the cast happens here; a null travels as it is.
export const numericValues = values => Object.fromEntries(
    Object.entries(values)
        .filter(([, value]) => value !== '' && value !== undefined)
        .map(([key, value]) => [key, value === null ? null : Number(value)])
);

export const CURRENT_KINDS = ['I1', 'I2', 'I3'];
const PF_KIND_NAMES = ['PF1', 'PF2', 'PF3'];

const isPfOutOfRange = (numericValue, readingKindName) => {
    if (!PF_KIND_NAMES.includes(readingKindName) || numericValue === null) {
        return false;
    }

    return numericValue < -0.99 || (numericValue > -0.85 && numericValue < 0.65) || numericValue > 1;
};

const isCurrentBelowMin = (numericValue, readingKindName, tiCurrent) => {
    if (!CURRENT_KINDS.includes(readingKindName) || numericValue === null) {
        return false;
    }

    if (Number.isNaN(tiCurrent)) {
        return false;
    }

    return numericValue < tiCurrent * 0.1;
};

// EN 50160: nominal voltage ±10% — 400V phase-to-phase, 230V phase-to-neutral
const VOLTAGE_RANGES = {
    U12: { min: 360, max: 440, nominal: 400 },
    U23: { min: 360, max: 440, nominal: 400 },
    U31: { min: 360, max: 440, nominal: 400 },
    U1N: { min: 207, max: 253, nominal: 230 },
    U2N: { min: 207, max: 253, nominal: 230 },
    U3N: { min: 207, max: 253, nominal: 230 }
};

const isVoltageBelowMin = (numericValue, readingKindName) => {
    const range = VOLTAGE_RANGES[readingKindName];
    if (!range || numericValue === null) {
        return false;
    }

    return numericValue < range.min;
};

const isVoltageAboveMax = (numericValue, readingKindName) => {
    const range = VOLTAGE_RANGES[readingKindName];
    if (!range || numericValue === null) {
        return false;
    }

    return numericValue > range.max;
};

const isCurrentAboveMax = (numericValue, readingKindName, tiCurrent) => {
    if (!CURRENT_KINDS.includes(readingKindName) || numericValue === null) {
        return false;
    }

    if (Number.isNaN(tiCurrent)) {
        return false;
    }

    return numericValue > tiCurrent;
};

export const getRangeNotes = (numericValue, readingKindName, tiCurrent) => {
    const notes = [];

    if (isPfOutOfRange(numericValue, readingKindName)) {
        notes.push('PF too low, under 0.65');
    }

    if (isCurrentBelowMin(numericValue, readingKindName, tiCurrent)) {
        notes.push('Current below 10% of TI Current range');
    }

    if (isCurrentAboveMax(numericValue, readingKindName, tiCurrent)) {
        notes.push('Current above TI Current range');
    }

    if (isVoltageBelowMin(numericValue, readingKindName)) {
        const range = VOLTAGE_RANGES[readingKindName];
        notes.push(`Voltage below ${range.min}V (${range.nominal}V -10%)`);
    }

    if (isVoltageAboveMax(numericValue, readingKindName)) {
        const range = VOLTAGE_RANGES[readingKindName];
        notes.push(`Voltage above ${range.max}V (${range.nominal}V +10%)`);
    }

    return notes.join('; ');
};

export const getVariationNote = variationValue => {
    if (variationValue === null || isVariationWithinLimit(variationValue)) {
        return '';
    }

    return `Current variation at or above ${VARIATION_LIMIT_PERCENT}%`;
};

export const getTechnicianComments = readings => {
    const said = (readings || []).map((reading, index) => ({ comment: reading.comments, number: index + 1 })).filter(({ comment }) => comment);
    if (said.length < 2) {
        return said[0]?.comment || '';
    }

    return said.map(({ comment, number }) => `#${number} ${comment}`).join('; ');
};

const getReadingRatio = reading => reading.extra.ratio || 1;

// The profile is the contract, exactly: primary from the compared rows, auxiliaries from the
// meter-face rows — bystander measurements stay out of the reading.
export const buildMeterValues = (systemValues, auxiliaryValues, primary, auxiliary) => ({
    ...Object.fromEntries(primary.map(kind => [kind, systemValues[kind]])),
    ...Object.fromEntries(auxiliary.map(kind => [kind, auxiliaryValues[kind]]))
});

export const getMeasurementFromReadingKindName = (readingKindName, measurements, readings) => {
    const reading = readings.find(r => r.reading_kind_name === readingKindName);
    if (!reading) {
        return null;
    }
    return measurements.find(m => m.reading_id === reading.id) || null;
};

export const formatRealtimeValue = (value, ratio = 1) => {
    const numberValue = value * ratio;
    return numberValue.toFixed(3);
};

// The box reports raw values and the API passes them on without a ratio, so the ratio is applied
// here, resolved the way the API resolves it for the bucket (Measurement#resolved_bucket_values):
// the measurement's own first, then its reading's. A pulse meter keeps it on the measurement -- the
// API allows it nowhere else -- while mbus and modbus_rtu keep it on the reading; reading only the
// reading's left every pulse meter's value as a raw count. The API's last resort, the reading of
// the port a measurement hangs from, is left out: that reading belongs to another device, and no
// verifiable measurement takes its ratio from there.
export const getMeasurementRatio = (measurement, readingsById) => (
    measurement.extra.ratio || getReadingRatio(readingsById[measurement.reading_id])
);

// The ratio a verification row shows, with the unit it converts into: 0.001 (Wh). A row with no
// measurement behind it has no ratio at all, which is not a ratio of 1. It is the ratio configured
// now: a submitted reading keeps the value it was converted to, not the ratio it was converted with.
export const displayRatio = (measurement, readingsById, unitSymbols) => {
    if (!measurement) {
        return '-';
    }

    const ratio = String(Number(getMeasurementRatio(measurement, readingsById)));
    const unit = unitSymbols?.[readingsById[measurement.reading_id]?.reading_kind_id];

    return unit ? `${ratio} (${unit})` : ratio;
};

// A channel the box reports without a value is no live value at all.
export const liveValue = (realtime, measurement) => {
    const match = measurement && realtime.find(item => item.id === measurement.id);
    return match && match.value !== null && match.value !== undefined ? match : null;
};

export const getRealtimeValue = (realtime, measurement, readingsById) => {
    const match = liveValue(realtime, measurement);
    return match ? formatRealtimeValue(match.value, getMeasurementRatio(measurement, readingsById)) : '';
};

export const formatVariation = value => (value === null || value === undefined ? '%' : `${value.toFixed(2)}%`);

export const getVariationValue = (systemValue, technicianValue) => {
    const systemNumber = parseFloat(systemValue);
    const technicianNumber = parseFloat(technicianValue);
    if (Number.isNaN(systemNumber) || Number.isNaN(technicianNumber) || systemNumber === 0) {
        return null;
    }
    return ((technicianNumber - systemNumber) / systemNumber) * 100;
};

// Mirrors the API's DeviceVerification::Concludable#delta_within_limit?.
export const getDeltaVariation = (system, technician, previous) => {
    if (!previous) {
        return null;
    }
    const systemDelta = parseFloat(system) - parseFloat(previous.system);
    const technicianDelta = parseFloat(technician) - parseFloat(previous.technician);
    if (isNaN(systemDelta) || isNaN(technicianDelta) || systemDelta === 0) {
        return null;
    }
    return ((technicianDelta - systemDelta) / systemDelta) * 100;
};

export const getVariation = (comparison, system, technician, previous) => (
    comparison === 'delta' ? getDeltaVariation(system, technician, previous) : getVariationValue(system, technician)
);

// The CT rating is the second line of defence: it only speaks when the two measurements agree.
const rangeChip = (kind, technician, tiCurrent) => {
    const percentage = `${(technician / tiCurrent * 100).toFixed(1)}%`;
    if (isCurrentBelowMin(technician, kind, tiCurrent)) {
        return { label: `TI${kind.slice(-1)} = ${percentage} (< 10%)`, variant: 'danger' };
    }
    if (isCurrentAboveMax(technician, kind, tiCurrent)) {
        return { label: `TI${kind.slice(-1)} = ${percentage} (> 100%)`, variant: 'danger' };
    }
    return null;
};

// Green when it holds, grey when there is nothing to compare, red with the reason — and a
// delta says only the colour, since its one reason is the deltas themselves.
const primaryChip = (kind, reading, previous, tiCurrent, comparison) => {
    const system = reading.meter_values[kind];
    const technician = reading.technician_values[kind];
    const variation = getVariation(comparison, system, technician, previous && { system: previous.meter_values[kind], technician: previous.technician_values[kind] });

    if (system === null || variation === null) {
        return { label: kind, variant: 'default' };
    }
    if (!isVariationWithinLimit(variation)) {
        return { label: comparison === 'delta' ? kind : `${kind} > ${Math.abs(variation).toFixed(1)}%`, variant: 'danger' };
    }
    return rangeChip(kind, technician, tiCurrent) || { label: kind, variant: 'success' };
};

// The verification's readings, oldest first — the verification payload itself carries none.
// One chip per primary, and one more if the meter's face had nothing to show somewhere.
export const getDataInfoChips = (readings, tiCurrent, comparison) => {
    const reading = readings?.at(-1);
    if (!reading) {
        return [];
    }

    const chips = Object.keys(reading.technician_values).map(kind => primaryChip(kind, reading, readings.at(-2), tiCurrent, comparison));
    const auxiliaryNotApplicable = Object.entries(reading.meter_values).some(([kind, value]) => value === null && !(kind in reading.technician_values));

    return auxiliaryNotApplicable ? [...chips, { label: 'N/A', variant: 'info' }] : chips;
};
